import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FiRefreshCw,
  FiEye,
  FiCalendar,
  FiCheck,
  FiX,
} from 'react-icons/fi';
import { API_ENDPOINTS } from '../../utils/api';
import { formatSessionDisplay, getPlanStatusBadgeClass } from '../../utils/dailyDoctorPlanUtils';

const formatDateISO = (d) => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
};

const PlanManagement = () => {
  const [selectedDate, setSelectedDate] = useState(formatDateISO(new Date()));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [viewModal, setViewModal] = useState({ open: false, entries: [], title: '', userId: null });
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const load = useCallback(async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(API_ENDPOINTS.getDailyDoctorPlansManagement(selectedDate), {
        headers: authHeaders(),
      });
      setData(res.users || res.employees || []);
    } catch (err) {
      console.error('PlanManagement load error:', err);
      Swal.fire('Error', err?.response?.data?.error || 'Failed to load plan management', 'error');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    load();
  }, [load]);

  const openView = (user) => {
    setViewModal({
      open: true,
      entries: user.entries || [],
      title: `${user.name} (${user.employeeId || user.phone || '—'})`,
      userId: user.userId,
    });
  };

  const closeView = () => setViewModal({ open: false, entries: [], title: '', userId: null });

  const handleStatusUpdate = async (entryId, status, userId) => {
    setStatusUpdatingId(entryId);
    try {
      await axios.patch(
        API_ENDPOINTS.updateDailyDoctorPlanStatus(entryId),
        { status },
        { headers: authHeaders() }
      );

      setViewModal((prev) => ({
        ...prev,
        entries: prev.entries.map((e) =>
          e._id === entryId ? { ...e, status } : e
        ),
      }));

      setData((prev) =>
        prev.map((user) => {
          if (String(user.userId) !== String(userId)) return user;
          return {
            ...user,
            entries: (user.entries || []).map((e) =>
              e._id === entryId ? { ...e, status } : e
            ),
          };
        })
      );
    } catch (err) {
      console.error('handleStatusUpdate error:', err);
      const label = status === 'Approved' ? 'approve' : 'reject';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.response?.data?.error || `Failed to ${label} entry`,
        customClass: { container: 'swal-above-app-modal' },
      });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const usersWithPlansCount = useMemo(() => data.filter((e) => (e.totalEntries || 0) > 0).length, [data]);

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-slate-100 via-teal-50/30 to-slate-100 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-3">
                <FiEye className="text-teal-600" />
                Plan Management
              </h1>
              <p className="text-slate-600 mt-1">
                All users — online status and daily plan entries for selected date
              </p>
            </div>

            <div className="flex items-start sm:items-center gap-2 flex-col sm:flex-row">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                <FiCalendar />
                Date
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-2 bg-white"
                />
              </label>
              <button
                type="button"
                onClick={load}
                className="inline-flex items-center gap-2 rounded-xl bg-[#1a365d] px-4 py-2 text-sm font-semibold text-white hover:bg-[#152c4d] transition-colors"
              >
                <FiRefreshCw />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-bold text-slate-800">All Users</h2>
            <span className="text-xs text-slate-500">
              {usersWithPlansCount} user(s) have entries · {data.length} total
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">SNO</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Number</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Role</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Online</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Number of entire</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500">Loading...</td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500">No users found</td>
                  </tr>
                ) : (
                  data.map((user, index) => (
                    <tr key={String(user.userId)} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-600">{String(index + 1).padStart(2, '0')}</td>
                      <td className="px-4 py-3 font-medium">{user.name}</td>
                      <td className="px-4 py-3 text-slate-600">{user.employeeId || user.phone || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 capitalize">{user.role || '—'}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            user.isOnline ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {user.isOnline ? 'Online' : 'Offline'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{user.totalEntries || 0}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => openView(user)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
                        >
                          <FiEye />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {viewModal.open && (
        <div className="fixed inset-0 bg-black/40 z-[2500] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-5xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-800">Entries - {viewModal.title}</h3>
              <button type="button" onClick={closeView} className="text-slate-500 hover:text-slate-800 px-2">
                ✕
              </button>
            </div>

            <div className="p-4 md:p-6">
              {viewModal.entries.length === 0 ? (
                <p className="text-slate-600">No entries for this date.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">SNO</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">Doctor</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">Location</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">Visits</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">Time</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">Feedback</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">Status / Review</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewModal.entries.map((e) => (
                        <tr key={e._id} className="border-b">
                          <td className="px-4 py-3 text-slate-600">{e.sno}</td>
                          <td className="px-4 py-3 font-medium">{e.doctorName}</td>
                          <td className="px-4 py-3 text-slate-600">{e.location}</td>
                          <td className="px-4 py-3 text-slate-600">{e.noOfVisits}</td>
                          <td className="px-4 py-3 text-slate-600">
                            {formatSessionDisplay(e.timeSession, e.timeFrom, e.timeTo)}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{e.feedback || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-2 min-w-[180px]">
                              <span
                                className={`inline-flex w-fit px-2 py-1 rounded-full text-xs font-semibold ${getPlanStatusBadgeClass(e.status)}`}
                              >
                                {e.status || 'Pending'}
                              </span>
                              <div className="inline-flex items-center gap-2 flex-wrap">
                                <button
                                  type="button"
                                  disabled={statusUpdatingId === e._id || e.status === 'Approved'}
                                  onClick={() => handleStatusUpdate(e._id, 'Approved', viewModal.userId)}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                                  title="Approve"
                                >
                                  <FiCheck />
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  disabled={statusUpdatingId === e._id || e.status === 'Rejected'}
                                  onClick={() => handleStatusUpdate(e._id, 'Rejected', viewModal.userId)}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                                  title="Reject"
                                >
                                  <FiX />
                                  Reject
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanManagement;

